import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { FileBadge, Loader2, Save, RotateCcw, AlertCircle } from 'lucide-react';

import {
    fetchHSNCategoryList,
    fetchHSNCodeList,
    fetchHSNDetails,
    submitHSNDetails,
    clearHSNDetails,
    clearHSNSaveResult,
    resetHSNCreation,
    selectHSNCategoryList,
    selectHSNCodeList,
    selectHSNDetails,
    selectHSNCategoryListLoading,
    selectHSNCodeListLoading,
    selectHSNDetailsLoading,
    selectHSNSaveLoading,
    selectHSNSaveResult,
    selectHSNSaveError,
} from '../../slices/purchaseSlice/hsnCreationSlice';
import { showToast } from '../../utilities/toastUtilities';

const TRANSACTION_TYPES = ['Creation', 'Updation'];
const CATEGORY_TYPES = ['Goods', 'Service'];

const inputCls =
    'w-full px-3 py-2.5 rounded-xl border-2 text-sm bg-white dark:bg-gray-800 text-gray-800 dark:text-gray-100 border-gray-200 dark:border-gray-700 focus:outline-none focus:ring-2 focus:border-indigo-500 focus:ring-indigo-100 dark:focus:ring-indigo-900/30 transition-all disabled:opacity-60';

const Label = ({ children, required }) => (
    <label className="block text-xs font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-1.5">
        {children}{required && <span className="text-rose-500 ml-0.5">*</span>}
    </label>
);

// Only digits, up to one decimal place — mirrors the legacy ValidateAmountQuantity keypress guard.
const isValidRateInput = (value) => value === '' || /^\d*\.?\d?$/.test(value);

const HSNCreation = () => {
    const dispatch = useDispatch();
    const { userData, userDetails } = useSelector((s) => s.auth);
    const currentUser = userData?.userName || userDetails?.userName || 'system';
    const roleId = userData?.roleId || userData?.RID || 0;

    const categoryList = useSelector(selectHSNCategoryList);
    const codeList      = useSelector(selectHSNCodeList);
    const details        = useSelector(selectHSNDetails);

    const categoryLoading = useSelector(selectHSNCategoryListLoading);
    const codeLoading     = useSelector(selectHSNCodeListLoading);
    const detailsLoading  = useSelector(selectHSNDetailsLoading);
    const saveLoading     = useSelector(selectHSNSaveLoading);
    const saveResult      = useSelector(selectHSNSaveResult);
    const saveError       = useSelector(selectHSNSaveError);

    const [transactionType, setTransactionType] = useState('');
    const [hsnCodeText, setHsnCodeText]     = useState('');   // Creation
    const [selectedHsnId, setSelectedHsnId] = useState('');   // Updation — Rowid
    const [categoryType, setCategoryType]   = useState('');   // Goods / Service
    const [hsnCategoryId, setHsnCategoryId] = useState('');
    const [cgst, setCgst] = useState('');
    const [sgst, setSgst] = useState('');
    const [igst, setIgst] = useState('');
    const [remarks, setRemarks] = useState('');
    const [formError, setFormError] = useState('');

    useEffect(() => {
        dispatch(fetchHSNCategoryList());
        return () => dispatch(resetHSNCreation());
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleTransactionTypeChange = (e) => {
        const value = e.target.value;
        setFormError('');
        setTransactionType(value);
        setHsnCodeText('');
        setSelectedHsnId('');
        setCategoryType('');
        setHsnCategoryId('');
        setCgst(''); setSgst(''); setIgst(''); setRemarks('');
        dispatch(clearHSNDetails());
        if (value === 'Updation') dispatch(fetchHSNCodeList());
    };

    const handleHsnCodeSelect = (e) => {
        const id = e.target.value;
        setSelectedHsnId(id);
        setCategoryType(''); setHsnCategoryId('');
        setCgst(''); setSgst(''); setIgst(''); setRemarks('');
        if (id) dispatch(fetchHSNDetails(id));
        else dispatch(clearHSNDetails());
    };

    // Prefill the form once GetHSNDetails resolves for the selected code
    useEffect(() => {
        if (!details) return;
        setCategoryType(details.Category || '');
        setHsnCategoryId(details.HSNCategory || '');
        setCgst(details.Cgstrate || '');
        setSgst(details.Sgstrate || '');
        setIgst(details.Igstrate || '');
        setRemarks(details.Remarks || '');
    }, [details]);

    const selectedHsnCode = useMemo(() => {
        const row = codeList.find((c) => String(c.hsnid) === String(selectedHsnId));
        return row?.hsnvalue || '';
    }, [codeList, selectedHsnId]);

    const handleReset = () => {
        setTransactionType('');
        setHsnCodeText('');
        setSelectedHsnId('');
        setCategoryType('');
        setHsnCategoryId('');
        setCgst(''); setSgst(''); setIgst(''); setRemarks('');
        setFormError('');
        dispatch(clearHSNDetails());
    };

    const handleSubmit = () => {
        if (!transactionType) { setFormError('Select Create New / Update'); return; }
        if (transactionType === 'Creation' && !hsnCodeText.trim()) { setFormError('Please Enter HSN Code'); return; }
        if (transactionType === 'Updation' && !selectedHsnId) { setFormError('Select HSN Code'); return; }
        if (!categoryType) { setFormError('Select Category Type'); return; }
        if (!hsnCategoryId) { setFormError('Select HSN Category'); return; }
        if (!cgst || parseFloat(cgst) <= 0) { setFormError('Please Enter CGST'); return; }
        if (!sgst || parseFloat(sgst) <= 0) { setFormError('Please Enter SGST'); return; }
        if (!igst || parseFloat(igst) <= 0) { setFormError('Please Enter IGST'); return; }
        if (!remarks.trim()) { setFormError('Please Enter Remarks'); return; }
        setFormError('');

        const hsnCategoryName = categoryList.find((c) => String(c.CategoryId) === String(hsnCategoryId))?.CategoryValue || '';

        dispatch(submitHSNDetails({
            TransactionType: transactionType,
            Category:        categoryType,
            HSNCategory:     hsnCategoryName,
            HSNCode:         transactionType === 'Creation' ? hsnCodeText.trim() : selectedHsnCode,
            Rowid:           transactionType === 'Updation' ? selectedHsnId : '',
            Cgstrate:        cgst,
            Sgstrate:        sgst,
            Igstrate:        igst,
            Remarks:         remarks,
            RoleID:          String(roleId),
            Createdby:       currentUser,
        }));
    };

    useEffect(() => {
        if (saveResult === 'Successfull') {
            showToast('success', 'HSN/SAC code submitted successfully.');
            dispatch(clearHSNSaveResult());
            handleReset();
        }
        if (saveError) {
            const msg = typeof saveError === 'string' ? saveError : 'Failed to save HSN details.';
            setFormError(msg);
            showToast('error', msg);
            dispatch(clearHSNSaveResult());
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [saveResult, saveError, dispatch]);

    return (
        <div className="min-h-screen bg-gradient-to-br from-slate-50 via-indigo-50/30 to-purple-50/30 dark:from-gray-950 dark:via-gray-900 dark:to-gray-950 p-4 md:p-6">

            {/* Page Header */}
            <div className="max-w-4xl mx-auto mb-6">
                <div className="relative overflow-hidden rounded-2xl bg-gradient-to-r from-indigo-600 via-purple-600 to-violet-700 shadow-xl shadow-indigo-500/20 p-6 text-white">
                    <div className="absolute top-0 right-0 w-64 h-64 bg-violet-500 rounded-full -translate-y-1/2 translate-x-1/4 opacity-20 blur-3xl" />
                    <div className="relative flex items-center gap-4">
                        <div className="w-12 h-12 rounded-2xl bg-white/15 backdrop-blur flex items-center justify-center shadow-lg border border-white/20 flex-shrink-0">
                            <FileBadge className="h-6 w-6 text-white" />
                        </div>
                        <div>
                            <span className="text-xs font-bold text-indigo-200 uppercase tracking-wider bg-white/10 px-2 py-0.5 rounded-full">
                                Configuration
                            </span>
                            <h1 className="text-xl md:text-2xl font-black tracking-tight mt-1">HSN / SAC Code Creation &amp; Updation</h1>
                            <p className="text-indigo-200 text-sm mt-0.5">
                                Register a new HSN/SAC code, or resubmit an existing one with updated GST rates.
                            </p>
                        </div>
                    </div>
                </div>
            </div>

            <div className="max-w-4xl mx-auto space-y-4">
                <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden">
                    <div className="px-4 py-3 bg-gray-50 dark:bg-gray-900/40 border-b border-gray-200 dark:border-gray-700 flex items-center gap-2">
                        <FileBadge className="h-4 w-4 text-indigo-500" />
                        <h2 className="text-sm font-bold text-gray-700 dark:text-gray-200">HSN / SAC Details</h2>
                    </div>

                    <div className="p-4 md:p-5 space-y-4">
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                            <div>
                                <Label required>Create New / Update</Label>
                                <select value={transactionType} onChange={handleTransactionTypeChange} className={inputCls}>
                                    <option value="">-Please Select-</option>
                                    {TRANSACTION_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
                                </select>
                            </div>

                            {transactionType === 'Creation' && (
                                <div>
                                    <Label required>HSN / SAC Code</Label>
                                    <input
                                        type="text"
                                        inputMode="numeric"
                                        maxLength={8}
                                        placeholder="Enter HSN/SAC code…"
                                        className={inputCls}
                                        value={hsnCodeText}
                                        onChange={(e) => setHsnCodeText(e.target.value.replace(/\D/g, ''))}
                                    />
                                </div>
                            )}

                            {transactionType === 'Updation' && (
                                <div>
                                    <Label required>HSN / SAC Code</Label>
                                    <div className="relative">
                                        <select value={selectedHsnId} onChange={handleHsnCodeSelect} disabled={codeLoading} className={inputCls}>
                                            <option value="">-Please Select-</option>
                                            {codeList.map((c) => (
                                                <option key={c.hsnid} value={c.hsnid}>{c.hsnvalue}</option>
                                            ))}
                                        </select>
                                        {(codeLoading || detailsLoading) && (
                                            <Loader2 className="absolute right-8 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-indigo-500 animate-spin" />
                                        )}
                                    </div>
                                </div>
                            )}

                            <div>
                                <Label required>Category Type</Label>
                                <select value={categoryType} onChange={(e) => { setFormError(''); setCategoryType(e.target.value); }} disabled={!transactionType} className={inputCls}>
                                    <option value="">-Please Select-</option>
                                    {CATEGORY_TYPES.map((c) => <option key={c} value={c}>{c}</option>)}
                                </select>
                            </div>

                            <div>
                                <Label required>HSN Category</Label>
                                <div className="relative">
                                    <select value={hsnCategoryId} onChange={(e) => { setFormError(''); setHsnCategoryId(e.target.value); }} disabled={!transactionType || categoryLoading} className={inputCls}>
                                        <option value="">-Please Select-</option>
                                        {categoryList.map((c) => (
                                            <option key={c.CategoryId} value={c.CategoryId}>{c.CategoryValue}</option>
                                        ))}
                                    </select>
                                    {categoryLoading && (
                                        <Loader2 className="absolute right-8 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-indigo-500 animate-spin" />
                                    )}
                                </div>
                            </div>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                            <div>
                                <Label required>CGST Rate (%)</Label>
                                <input type="text" inputMode="decimal" className={inputCls}
                                    value={cgst}
                                    onChange={(e) => { if (isValidRateInput(e.target.value)) { setFormError(''); setCgst(e.target.value); } }} />
                            </div>
                            <div>
                                <Label required>SGST Rate (%)</Label>
                                <input type="text" inputMode="decimal" className={inputCls}
                                    value={sgst}
                                    onChange={(e) => { if (isValidRateInput(e.target.value)) { setFormError(''); setSgst(e.target.value); } }} />
                            </div>
                            <div>
                                <Label required>IGST Rate (%)</Label>
                                <input type="text" inputMode="decimal" className={inputCls}
                                    value={igst}
                                    onChange={(e) => { if (isValidRateInput(e.target.value)) { setFormError(''); setIgst(e.target.value); } }} />
                            </div>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 items-end">
                            <div className="md:col-span-2">
                                <Label required>Remarks</Label>
                                <textarea rows={2} className={inputCls}
                                    value={remarks} onChange={(e) => { setFormError(''); setRemarks(e.target.value); }} />
                            </div>

                            <div className="flex items-center gap-2">
                                <button
                                    onClick={handleSubmit}
                                    disabled={saveLoading}
                                    className="inline-flex items-center justify-center gap-1.5 px-5 py-2.5 rounded-xl text-sm font-semibold text-white bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                                >
                                    {saveLoading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5" />}
                                    Submit
                                </button>
                                <button
                                    onClick={handleReset}
                                    disabled={saveLoading}
                                    className="inline-flex items-center justify-center gap-1.5 px-4 py-2.5 rounded-xl text-sm font-semibold text-gray-600 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 disabled:opacity-50 transition-colors"
                                >
                                    <RotateCcw className="h-3.5 w-3.5" />
                                    Reset
                                </button>
                            </div>
                        </div>

                        {formError && (
                            <div className="flex items-center gap-2 px-3 py-2 rounded-xl bg-rose-50 dark:bg-rose-900/10 border border-rose-200 dark:border-rose-800/50 text-sm text-rose-600 dark:text-rose-400">
                                <AlertCircle className="h-4 w-4 flex-shrink-0" />
                                {formError}
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default HSNCreation;
